import {
    Box,
    Button,
    Divider,
    FormControlLabel,
    Grid,
    Switch,
    Typography,
    useTheme,
} from "@mui/material";
import * as Yup from "yup";
import React, {
    Dispatch,
    SetStateAction,
    useEffect,
    useRef,
    useState,
} from "react";
import { FieldArray, Form, Formik, FormikProps } from "formik";
import { PolicySavePayloadDTO } from "../../types";
import FormikDropdown from "../../components/Input/FormikDropdown";
import MyText from "../../components/Input/MyText";
import { LoadingButton } from "@mui/lab";
import { useStateValue } from "../../util/stateProvider";
import { policyService } from "../../services/api/policyServics";
import dayjs from "dayjs";
import Iconify from "../../components/iconify";

type Props = {
    onSubmit: (payload: PolicySavePayloadDTO) => void;
    policyData: PolicySavePayloadDTO | null;
    isLoading: boolean;
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    customer: {
        label: string;
        value: string;
    };
};

export const getValidationSchema = () => {
    return Yup.object({
        insuranceCategory: Yup.string().required(
            "Insurance category is required"
        ),
        policyType: Yup.string().required("Policy type is required"),
        insurer: Yup.string(),
        startDate: Yup.date().required("Start date is required").nullable(),
        endDate: Yup.date().required("End date is required").nullable(),
        policyId: Yup.string(),
        netPremium: Yup.number()
            .required("Net premium is required")
            .typeError("Net premium must be a number"),
        gstPremium: Yup.number().typeError("GST amount must be a number"),
        policyPremium: Yup.number().typeError("Total premium must be a number"),
        si: Yup.number(),
    });
};

const HealthPolicyOptions = [
    {
        id: "BASE",
        name: "BASE",
    },
    {
        id: "TOPUP",
        name: "TOPUP",
    },
];

export enum Relationships {
    Self = "Self",
    Wife = "Wife",
    Daughter = "Daughter",
    Son = "Son",
    Father = "Father",
    Mother = "Mother",
    Husband = "Husband",
}

export type FormValues = {
    insuranceCategory: string;
    policyType: string | undefined;
    insurer: string | undefined;
    startDate: Date | null;
    endDate: Date | null;
    policyId: string | null | undefined;
    netPremium: number | undefined;
    gstPremium: number | undefined;
    policyPremium: number | undefined;
    tpExpiryDate?: Date | null;
    make?: string;
    model?: string;
    memberDTOS?: {
        name: string;
        dob: Date | null;
        relationship: string;
        policyInceptionDate: Date | null;
        sumInsured?: number;
        deductible?: number;
    }[];
    registrationNumber?: string;
    commercialType?: number;
    idv?: number;
    coverPeriod?: number;
    si?: number;
    isFloater?: boolean;
    sumInsured?: number;
    deductible?: number;
};

export function getRelationshipOptions(): { id: string; name: string }[] {
    return Object.values(Relationships).map((relationship) => ({
        id: relationship,
        name: relationship,
    }));
}

export function getGenderByRelationship(relationship: Relationships) {
    switch (relationship) {
        case Relationships.Wife:
        case Relationships.Daughter:
        case Relationships.Mother:
            return "Female";
        default:
            return "Male";
    }
}

export default function HealthPolicyForm({
    onSubmit,
    policyData,
    isLoading,
    setIsLoading,
    customer,
}: Props) {
    const formikRef = useRef<
        FormikProps<{
            policyNumber: string;
        }>
    >(null);
    const theme = useTheme();
    const [{ user }] = useStateValue();

    let agentUuid = user?.agentUuid || null;
    const [isFetched, setIsFetched] = useState({
        insurer: false,
    });
    const [insurerOptions, setInsurerOptions] = useState<
        { id: string; name: string }[]
    >([]);

    const fetchInsurerOptions = () => {
        if (agentUuid && !isFetched.insurer) {
            policyService.getInsurerList(agentUuid).then((res) => {
                if (res && res.data.response.length) {
                    setInsurerOptions(
                        res.data.response.map((insurer) => ({
                            id: insurer.value,
                            name: insurer.label,
                        }))
                    );
                    setIsFetched((prev) => ({ ...prev, insurer: true }));
                }
            });
        }
    };

    const handleSubmit = (values: FormValues) => {
        if (!customer || !customer.value) {
            console.error("Lead UUID is not available");
            return;
        }

        if (!values.policyType) return;

        setIsLoading(true);
        let payload: PolicySavePayloadDTO = {
            ...policyData,
            client: {
                label: customer?.label,
                value: customer?.value,
            },
            flow: 3,
            businessType: 1,
            insuranceCategory: values.insuranceCategory,
            policyType: values.policyType,
            insurer: values.insurer,
            policyId: values.policyId,
            startDate: values.startDate
                ? dayjs(values.startDate).format("YYYY-MM-DD")
                : "",
            endDate: values.endDate
                ? dayjs(values.endDate).format("YYYY-MM-DD")
                : "",
            coverPeriod: values.coverPeriod,
            netPremium: values.netPremium || 0,
            gstPremium: values.gstPremium || 0,
            policyPremium: values.policyPremium || 0,
            healthPolicyDTO: {
                sumInsured: values?.isFloater ? values.sumInsured || 0 : 0,
                deductible: values?.isFloater ? values.deductible || 0 : 0,
            },
            type: "policy",
        };

        let members = (values.memberDTOS || []).map((member) => ({
            name: member.name,
            dob: member.dob ? dayjs(member.dob).format("YYYY-MM-DD") : "",
            relationship: member.relationship,
            gender: getGenderByRelationship(
                member.relationship as Relationships
            ),
        }));

        if (members?.length > 0) {
            policyService
                .saveCustomerPolicyMembers(members, customer?.value)
                .then((res) => {
                    if (
                        res &&
                        res.data.response &&
                        res.data.response?.d_client_members?.length > 0
                    ) {
                        payload.memberDTOS =
                            res.data.response?.d_client_members.map(
                                (member: any) => {
                                    let policyInceptionDate =
                                        values.memberDTOS?.find(
                                            (m) => m.name === member.name
                                        )?.policyInceptionDate ||
                                        values.startDate;

                                    let memberData = values.memberDTOS?.find(
                                        (m) => m.name === member.name
                                    );

                                    return {
                                        memberId: member.id,
                                        name: member.name,
                                        dob: member.dob
                                            ? dayjs(member.dob).format(
                                                  "YYYY-MM-DD"
                                              )
                                            : "",
                                        relationship: member.relationship,
                                        policyInceptionDate: policyInceptionDate
                                            ? dayjs(policyInceptionDate).format(
                                                  "YYYY-MM-DD"
                                              )
                                            : "",
                                        sumInsured: !values?.isFloater
                                            ? memberData?.sumInsured || 0
                                            : 0,
                                        deductible: !values?.isFloater
                                            ? memberData?.deductible || 0
                                            : 0,
                                        ped: "",
                                        permanentTotalDisability: 0,
                                        permanentPartialDisability: 0,
                                        weeklyBenefits: 0,
                                    };
                                }
                            );

                        onSubmit(payload);
                    }
                });
        } else {
            onSubmit(payload);
        }
    };

    return (
        <>
            <Formik
                // @ts-ignore
                ref={formikRef}
                initialValues={{
                    insuranceCategory: "Health",
                    policyType: policyData?.policyType
                        ? HealthPolicyOptions.find(
                              (p) => p.id === policyData?.policyType
                          )?.id
                        : undefined,
                    insurer: policyData?.insurer,
                    startDate: policyData?.startDate
                        ? new Date(policyData?.startDate)
                        : null,
                    endDate: policyData?.endDate
                        ? new Date(policyData?.endDate)
                        : null,
                    policyId: policyData?.policyId,
                    netPremium: policyData?.netPremium,
                    gstPremium: policyData?.gstPremium,
                    policyPremium: policyData?.policyPremium,
                    memberDTOS: (policyData?.memberDTOS || [])?.map(
                        (member) => {
                            let policyInceptionDate = member.policyInceptionDate
                                ? new Date(member.policyInceptionDate)
                                : policyData?.startDate
                                ? new Date(policyData?.startDate)
                                : null;

                            return {
                                name: member.name,
                                dob: member.dob ? new Date(member.dob) : null,
                                relationship: member.relationship || "",
                                policyInceptionDate: policyInceptionDate,
                            };
                        }
                    ),
                    sumInsured:
                        policyData?.healthPolicyDTO?.sumInsured ||
                        policyData?.sumInsured ||
                        0,
                    deductible:
                        policyData?.healthPolicyDTO?.deductible ||
                        policyData?.deductible ||
                        0,
                    isFloater: policyData?.isFloater || true,
                }}
                validationSchema={getValidationSchema().shape({
                    memberDTOS: Yup.array().of(
                        Yup.object().shape({
                            name: Yup.string().required("Name is required"),
                            relationship: Yup.string().required(
                                "Relationship is required"
                            ),
                            dob: Yup.string().nullable(),
                            policyInceptionDate: Yup.string().nullable(),
                            sumInsured: Yup.number(),
                            deductible: Yup.number(),
                        })
                    ),
                })}
                onSubmit={(values) => {
                    console.log("Form submitted with values:", values);

                    handleSubmit(values);
                }}
            >
                {({ isSubmitting, errors, values, setFieldValue }) => {
                    console.log("errors", errors);

                    return (
                        <>
                            <Form>
                                <Box
                                    sx={{
                                        minHeight: { xs: "100%", sm: "456px" },
                                    }}
                                >
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1">
                                                Basic policy details
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormikDropdown
                                                title="Insurance Category"
                                                name="insuranceCategory"
                                                className=" dropDownSuper colorUpdate"
                                                defaultVal={"Health"}
                                                items={[
                                                    {
                                                        id: "Health",
                                                        name: "Health",
                                                    },
                                                ]}
                                                change={() => {}}
                                                disabled={true}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormikDropdown
                                                title="Policy Type"
                                                name="policyType"
                                                className=" dropDownSuper colorUpdate"
                                                defaultVal={
                                                    policyData?.policyType
                                                }
                                                items={HealthPolicyOptions}
                                                change={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormikDropdown
                                                title="insurer"
                                                name="insurer"
                                                className=" dropDownSuper colorUpdate"
                                                defaultVal={policyData?.insurer}
                                                items={
                                                    isFetched.insurer
                                                        ? insurerOptions
                                                        : policyData?.insurer
                                                        ? [
                                                              {
                                                                  id: policyData.insurer,
                                                                  name: policyData.insurer,
                                                              },
                                                          ]
                                                        : []
                                                }
                                                change={() => {}}
                                                onOpen={() => {
                                                    fetchInsurerOptions();
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="Start date *"
                                                name="startDate"
                                                type="date"
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="End date *"
                                                name="endDate"
                                                type="date"
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="Policy number"
                                                name="policyId"
                                                type="text"
                                                className="no-input-padding"
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={
                                                            values.isFloater
                                                        }
                                                        onChange={(e) =>
                                                            setFieldValue(
                                                                "isFloater",
                                                                e.target.checked
                                                            )
                                                        }
                                                        name="isFloater"
                                                    />
                                                }
                                                label="This is a floater policy"
                                            />
                                        </Grid>
                                        {values.isFloater && (
                                            <>
                                                <Grid item xs={12} sm={6}>
                                                    <MyText
                                                        label="Sum insured"
                                                        name="sumInsured"
                                                        type="text"
                                                        className=""
                                                        onChange={() => {}}
                                                        autoFocus
                                                    />
                                                </Grid>
                                                {values.policyType ===
                                                    "TOPUP" && (
                                                    <Grid item xs={12} sm={6}>
                                                        <MyText
                                                            label="Deductible"
                                                            name="deductible"
                                                            type="text"
                                                            className=""
                                                            onChange={() => {}}
                                                        />
                                                    </Grid>
                                                )}
                                            </>
                                        )}
                                        <Grid item xs={12}>
                                            <Button
                                                size="small"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    // add extra member form to existing members
                                                    setFieldValue(
                                                        "memberDTOS",
                                                        [
                                                            ...(values.memberDTOS ||
                                                                []),
                                                            {
                                                                name: "",
                                                                dob: null,
                                                                relationship:
                                                                    "",
                                                                policyInceptionDate:
                                                                    values.startDate ||
                                                                    null,
                                                            },
                                                        ]
                                                    );
                                                }}
                                                variant="outlined"
                                                sx={{
                                                    color: theme.palette?.brand
                                                        ?.main,
                                                    borderColor:
                                                        theme.palette?.brand
                                                            ?.main,
                                                    "&:hover": {
                                                        borderColor:
                                                            theme.palette?.brand
                                                                ?.main,
                                                    },
                                                }}
                                            >
                                                Add member
                                            </Button>
                                        </Grid>
                                        <FieldArray name="members">
                                            {({ push, remove, form }) => (
                                                <>
                                                    {form.values.memberDTOS.map(
                                                        (
                                                            member: {
                                                                name: string;
                                                                dob: string;
                                                                relationship: string;
                                                                policyInceptionDate: string;
                                                            },
                                                            index: number
                                                        ) => (
                                                            <React.Fragment
                                                                key={`index-${index}-{member.name}`}
                                                            >
                                                                <Grid
                                                                    item
                                                                    xs={10}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        sx={{
                                                                            mt: 2,
                                                                        }}
                                                                    >
                                                                        Member{" "}
                                                                        {index +
                                                                            1}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    xs={2}
                                                                    sx={{
                                                                        display:
                                                                            "flex",
                                                                        justifyContent:
                                                                            "flex-end",
                                                                        alignItems:
                                                                            "center",
                                                                    }}
                                                                    title="Remove member"
                                                                >
                                                                    <Iconify
                                                                        sx={{
                                                                            cursor: "pointer",
                                                                            color: theme
                                                                                .palette
                                                                                .error
                                                                                .main,
                                                                        }}
                                                                        icon="solar:trash-bin-trash-bold"
                                                                        onClick={() => {
                                                                            // remove member from memberDTOS array need to remove from form.values.memberDTOS always
                                                                            const updatedMembers =
                                                                                [
                                                                                    ...form
                                                                                        .values
                                                                                        .memberDTOS,
                                                                                ];
                                                                            updatedMembers.splice(
                                                                                index,
                                                                                1
                                                                            );
                                                                            setFieldValue(
                                                                                "memberDTOS",
                                                                                updatedMembers
                                                                            );
                                                                            remove(
                                                                                index
                                                                            );
                                                                        }}
                                                                    />
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    sm={6}
                                                                >
                                                                    <MyText
                                                                        label="Name *"
                                                                        name={`memberDTOS[${index}].name`}
                                                                        type="text"
                                                                        onChange={() => {}}
                                                                    />
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    sm={6}
                                                                >
                                                                    <FormikDropdown
                                                                        title="Relationship *"
                                                                        name={`memberDTOS[${index}].relationship`}
                                                                        className=" dropDownSuper colorUpdate"
                                                                        defaultVal={
                                                                            null
                                                                        }
                                                                        items={getRelationshipOptions()}
                                                                        change={() => {}}
                                                                    />
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    sm={6}
                                                                >
                                                                    <MyText
                                                                        label="Date of Birth"
                                                                        name={`memberDTOS[${index}].dob`}
                                                                        type="date"
                                                                        onChange={() => {}}
                                                                    />
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    sm={6}
                                                                >
                                                                    <MyText
                                                                        label="Date of inception"
                                                                        name={`memberDTOS[${index}].policyInceptionDate`}
                                                                        type="date"
                                                                        onChange={() => {}}
                                                                    />
                                                                </Grid>
                                                                {!values.isFloater && (
                                                                    <>
                                                                        <Grid
                                                                            item
                                                                            xs={
                                                                                12
                                                                            }
                                                                            sm={
                                                                                6
                                                                            }
                                                                        >
                                                                            <MyText
                                                                                label="Sum insured"
                                                                                name={`memberDTOS[${index}].sumInsured`}
                                                                                type="text"
                                                                                onChange={() => {}}
                                                                            />
                                                                        </Grid>
                                                                        {values.policyType ===
                                                                            "TOPUP" && (
                                                                            <Grid
                                                                                item
                                                                                xs={
                                                                                    12
                                                                                }
                                                                                sm={
                                                                                    6
                                                                                }
                                                                            >
                                                                                <MyText
                                                                                    label="Deductible"
                                                                                    name={`memberDTOS[${index}].deductible`}
                                                                                    type="text"
                                                                                    onChange={() => {}}
                                                                                />
                                                                            </Grid>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </React.Fragment>
                                                        )
                                                    )}
                                                </>
                                            )}
                                        </FieldArray>

                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1">
                                                Premium details
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="Net premium *"
                                                name="netPremium"
                                                type="text"
                                                className=""
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="GST amount"
                                                name="gstPremium"
                                                type="text"
                                                className=""
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="Total premium"
                                                name="policyPremium"
                                                type="text"
                                                className=""
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Divider
                                    variant="dashed"
                                    sx={{
                                        borderColor: "grey.400",
                                        opacity: 0.3,
                                        my: "16px",
                                    }}
                                />
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        alignItems: "center",
                                        flexDirection: {
                                            xs: "column",
                                            sm: "row",
                                        },
                                    }}
                                >
                                    <LoadingButton
                                        size="small"
                                        variant="contained"
                                        sx={{
                                            marginLeft: { xs: 0, sm: "auot" },
                                            alignSelf: {
                                                xs: "center",
                                                sm: "flex-end",
                                            },
                                            background:
                                                theme.palette.brand.main,
                                            width: { xs: "100%", sm: "auto" },
                                        }}
                                        loading={isLoading}
                                        disabled={isSubmitting}
                                        type="submit"
                                    >
                                        Submit
                                    </LoadingButton>
                                </Box>
                            </Form>
                        </>
                    );
                }}
            </Formik>
        </>
    );
}
